import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { ChartData } from 'lib/api/reports/types';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import { SummaryBoxProps } from 'pages/Home/components/common/Prospects/Prospects.types';
import React, { useEffect, useState } from 'react';

import LineChart, { aggregateByDate, dataFilters } from '../LineChart';

const filterNameStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '4px',
}

const useStyles = makeStyles(() =>
  createStyles({
    box: {
      border: '1px solid #e0e0e0',
      padding: '30px 0px',
      background: '#ffffff',
      borderRadius: '10px',
      width: '100%', // ensure the table fits its container
    },
    numberBox: {
      border: '1px solid #e0e0e0',
      padding: '0px',
      background: '#ffffff',
      borderRadius: '10px',
      width: '100%', // ensure the table fits its container
      tableLayout: 'fixed', // this can help with column overflow issues
      textAlign: 'center',
    },
  }),
);

const SummaryBox: React.FC<SummaryBoxProps> = ({ content, children }) => {
  const classes = useStyles();
  return (
    <div className={classes.box}>
      {/* <Typography variant="h4">{title}</Typography> */}
      {content}
      {children}
    </div>
  );
};

const MainGraph = ({
  handleSelectChartDataFilter,
  chartDataFiltered,
  chartData,
  businessesNames,
  stakeholdersNames,
  executivesNames,
  clientsNames,
  setBusinessesNames,
  setStakeholdersNames,
  setExecutivesNames,
  setClientsNames,
}: {
  handleSelectChartDataFilter: ({
    selectedFilters,
    newChartData,
  }: {
    selectedFilters: string[];
    newChartData?: ChartData[];
  }) => void;
  chartDataFiltered: ChartData[];
  chartData: ChartData[];
  businessesNames: string[];
  stakeholdersNames: string[];
  executivesNames: string[];
  clientsNames: string[];
  setBusinessesNames: (businessesNames: string[]) => void;
  setStakeholdersNames: (stakeholdersNames: string[]) => void;
  setExecutivesNames: (executivesNames: string[]) => void;
  setClientsNames: (clientsNames: string[]) => void;
}) => {
  const [setUp, setSetUp] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedBusiness, setSelectedBusiness] = useState<string>('all');
  const [selectedStakeholder, setSelectedStakeholder] = useState<string>('all');
  const [selectedExecutive, setSelectedExecutive] = useState<string>('all');
  const [isCompareMode, setIsCompareMode] = useState<boolean>(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState<boolean>(false);
  const [compareCategory, setCompareCategory] = useState<string>('business');
  const [comparePlacementType, setComparePlacementType] =
    useState<string>('Colocación total');
  const [compareItems, setCompareItems] = useState<string[]>([]);
  const [tableDialogOpen, setTableDialogOpen] = useState<boolean>(false);

  const getCompareOptions = () => {
    switch (compareCategory) {
      case 'business':
        return businessesNames;
      case 'stakeholder':
        return stakeholdersNames;
      case 'executive':
        return executivesNames;
      default:
        return [];
    }
  };

  const generateCompareData = () => {
    if (!isCompareMode || compareItems.length === 0) {
      return chartDataFiltered;
    }

    const compareData: Record<string, string | number>[] = [];

    // Para cada item seleccionado para comparar
    compareItems.forEach((item) => {
      let filteredByItem = chartData;

      // Filtrar por el item específico según la categoría
      switch (compareCategory) {
        case 'business':
          filteredByItem = filteredByItem.filter(
            (data) => data.businessName === item,
          );
          break;
        case 'stakeholder':
          filteredByItem = filteredByItem.filter(
            (data) => data.stakeholderName === item,
          );
          break;
        case 'executive':
          filteredByItem = filteredByItem.filter(
            (data) => data.executiveName === item,
          );
          break;
      }

      // Agregar por fecha
      const aggregatedData = aggregateByDate(filteredByItem);

      // Transformar para el gráfico de comparación
      aggregatedData.forEach((data) => {
        const existingData = compareData.find((d) => d.name === data.name);
        const value = data[comparePlacementType as keyof ChartData];

        if (existingData) {
          existingData[`${item} - ${comparePlacementType}`] = value as
            | string
            | number;
        } else {
          compareData.push({
            name: data.name,
            [`${item} - ${comparePlacementType}`]: value as string | number,
          });
        }
      });
    });

    // Ordenar por fecha (name) - convertir a Date para ordenar correctamente
    compareData.sort((a, b) => {
      const dateA = new Date(a.name as string);
      const dateB = new Date(b.name as string);
      return dateA.getTime() - dateB.getTime();
    });

    return compareData;
  };

  const handleCompareStart = () => {
    if (compareItems.length > 0) {
      setIsCompareMode(true);
      setCompareDialogOpen(false);
    }
  };

  const handleCompareCancel = () => {
    setIsCompareMode(false);
    setCompareDialogOpen(false);
    setCompareItems([]);
  };

  const getTableData = () => {
    // Obtener los datos que se están mostrando actualmente en el gráfico
    let currentData;
    if (isCompareMode) {
      currentData = generateCompareData();
    } else {
      // En modo normal, primero filtramos y luego agregamos
      const filteredData = filterData();
      currentData = aggregateByDate(filteredData);
    }

    // Transformar a formato de tabla
    return currentData.map((item: any, index: number) => {
      const row: any = {
        id: index,
        Fecha: item.name,
      };

      // Agregar todas las columnas dinámicamente
      Object.keys(item).forEach((key) => {
        if (
          key !== 'name' &&
          key !== 'businessName' &&
          key !== 'stakeholderName' &&
          key !== 'executiveName'
        ) {
          row[key] = item[key];
        }
      });

      return row;
    });
  };

  const getTableHeaders = () => {
    let currentData;
    if (isCompareMode) {
      currentData = generateCompareData();
    } else {
      // En modo normal, primero filtramos y luego agregamos
      const filteredData = filterData();
      currentData = aggregateByDate(filteredData);
    }
    if (currentData.length === 0) return [];

    const headers: any[] = [
      {
        key: 'Fecha',
        label: 'Fecha',
        align: 'left',
      },
    ];

    // Obtener todas las columnas dinámicamente
    Object.keys(currentData[0]).forEach((key) => {
      if (
        key !== 'name' &&
        key !== 'businessName' &&
        key !== 'stakeholderName' &&
        key !== 'executiveName'
      ) {
        headers.push({
          key: key,
          label: key,
          align: 'right',
          type: 'money',
        });
      }
    });

    return headers;
  };

  const filterData = () => {
    let filteredData = chartData;

    if (selectedBusiness !== 'all') {
      filteredData = filteredData.filter(
        (item) => item.businessName === selectedBusiness,
      );
    }

    if (selectedClient !== 'all') {
      filteredData = filteredData.filter(
        (item) => item.clientName === selectedClient,
      );
    }

    if (selectedStakeholder !== 'all') {
      filteredData = filteredData.filter(
        (item) => item.stakeholderName === selectedStakeholder,
      );
    }

    if (selectedExecutive !== 'all') {
      filteredData = filteredData.filter(
        (item) => item.executiveName === selectedExecutive,
      );
    }

    return filteredData;
  };

  const updateFilterOptions = () => {
    let filtered = chartData;

    if (selectedExecutive !== 'all') {
      filtered = filtered.filter(
        (item) => item.executiveName === selectedExecutive,
      );
    }

    if (selectedBusiness !== 'all') {
      filtered = filtered.filter(
        (item) => item.businessName === selectedBusiness,
      );
    }

    if (selectedClient !== 'all') {
      filtered = filtered.filter(
        (item) => item.clientName === selectedClient,
      );
    }

    if (selectedStakeholder !== 'all') {
      filtered = filtered.filter(
        (item) => item.stakeholderName === selectedStakeholder,
      );
    }

    // Actualiza los sets dinámicamente según el filtrado
    const businessesSet = new Set<string>();
    const stakeholdersSet = new Set<string>();
    const executivesSet = new Set<string>();

    for (const item of filtered) {
      businessesSet.add(item.businessName);
      stakeholdersSet.add(item.stakeholderName);
      executivesSet.add(item.executiveName);
    }

    setBusinessesNames(Array.from(businessesSet));
    setStakeholdersNames(Array.from(stakeholdersSet));
    setExecutivesNames(Array.from(executivesSet));
  };

  const handleSetUp = () => {
    const filteredData = filterData();
    handleSelectChartDataFilter({
      selectedFilters: dataFilters,
      newChartData: filteredData,
    });
    setSetUp(true);
  };

  useEffect(() => {
    handleSetUp();
  }, [
    selectedBusiness,
    selectedClient,
    selectedStakeholder,
    selectedExecutive,
    chartData,
    isCompareMode,
    compareItems,
    compareCategory,
    comparePlacementType,
  ]);

  useEffect(() => {
    updateFilterOptions();
  }, [selectedBusiness, selectedStakeholder, selectedExecutive, selectedClient]);

  if (!setUp) {
    return <CenteredCircularProgress type="layout" />;
  }

  return (
    <>
      <SummaryBox
        title={`Histórico de colocación`}
        content={
          <>
            {/* Header con botón de comparar */}
            <div
              style={{
                padding: '0px 8px 10px 8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Filtros por Business, Stakeholder y Executive */}
              <div
                style={{
                  padding: '0px 8px 10px 8px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <div style={filterNameStyle}>
                  <Typography variant="body2">Cliente</Typography>
                  <Select
                    value={selectedClient}
                    onChange={(event) =>
                      setSelectedClient(event.target.value)
                    }
                    style={{ width: '200px', height: '30px' }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {clientsNames.map((client) => (
                      <MenuItem key={client} value={client}>
                        {client}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div style={filterNameStyle}>
                  <Typography variant="body2">Emisor</Typography>
                  <Select
                    value={selectedBusiness}
                    onChange={(event) =>
                      setSelectedBusiness(event.target.value)
                    }
                    style={{ width: '200px', height: '30px' }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {businessesNames.map((business) => (
                      <MenuItem key={business} value={business}>
                        {business}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div style={filterNameStyle}>
                  <Typography variant="body2">Deudor</Typography>
                  <Select
                    value={selectedStakeholder}
                    onChange={(event) =>
                      setSelectedStakeholder(event.target.value)
                    }
                    style={{ width: '200px', height: '30px' }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {stakeholdersNames.map((stakeholder) => (
                      <MenuItem key={stakeholder} value={stakeholder}>
                        {stakeholder}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div style={filterNameStyle}>
                  <Typography variant="body2">Ejecutivo</Typography>
                  <Select
                    value={selectedExecutive}
                    onChange={(event) =>
                      setSelectedExecutive(event.target.value)
                    }
                    style={{ width: '200px', height: '30px' }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {executivesNames.map((executive) => (
                      <MenuItem key={executive} value={executive}>
                        {executive}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <CustomButton
                  color="secondary"
                  onClick={() => setTableDialogOpen(true)}
                >
                  Tabla
                </CustomButton>
                <CustomButton
                  color={isCompareMode ? 'primary' : 'secondary'}
                  onClick={() => setCompareDialogOpen(true)}
                >
                  {isCompareMode ? 'Modo Comparación' : 'Comparar'}
                </CustomButton>
              </div>
            </div>
            <LineChart
              aspect={4.0 / 0.8}
              data={isCompareMode ? generateCompareData() : chartDataFiltered}
              skipAggregation={isCompareMode}
            />
          </>
        }
      />
      <Dialog
        open={compareDialogOpen}
        onClose={handleCompareCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configurar Comparación</DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              padding: '20px 0',
            }}
          >
            {/* Selección de categoría */}
            <FormControl fullWidth>
              <InputLabel>Categoría a comparar</InputLabel>
              <Select
                value={compareCategory}
                onChange={(event) => setCompareCategory(event.target.value)}
                label="Categoría a comparar"
              >
                <MenuItem value="business">Cliente</MenuItem>
                <MenuItem value="stakeholder">Deudor</MenuItem>
                <MenuItem value="executive">Ejecutivo</MenuItem>
              </Select>
            </FormControl>

            {/* Selección de tipo de colocación */}
            <FormControl fullWidth>
              <InputLabel>Tipo de colocación</InputLabel>
              <Select
                value={comparePlacementType}
                onChange={(event) =>
                  setComparePlacementType(event.target.value)
                }
                label="Tipo de colocación"
              >
                {dataFilters.map((filter) => (
                  <MenuItem key={filter} value={filter}>
                    {filter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Selección de items a comparar */}
            <FormControl fullWidth>
              <InputLabel>Items a comparar</InputLabel>
              <Select
                multiple
                value={compareItems}
                onChange={(event) =>
                  setCompareItems(event.target.value as string[])
                }
                label="Items a comparar"
                renderValue={(selected) => selected.join(', ')}
              >
                {getCompareOptions().map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={handleCompareCancel}>
            {isCompareMode ? 'Terminar' : 'Cerrar'}
          </CustomButton>
          <CustomButton
            onClick={handleCompareStart}
            variant="contained"
            disabled={compareItems.length === 0}
          >
            Comparar
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* Modal de Tabla */}
      <Dialog
        open={tableDialogOpen}
        onClose={() => setTableDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isCompareMode ? 'Datos de Comparación' : 'Datos del Gráfico'}
        </DialogTitle>
        <DialogContent>
          <div style={{ padding: '20px 0' }}>
            <EnhancedTable
              data={getTableData()}
              dataHeaders={getTableHeaders()}
              rowsPerPageDefault={10}
              exportToFile="xlsx"
              fileName={`reporte_colocaciones_${new Date().toISOString().split('T')[0]}.xlsx`}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setTableDialogOpen(false)}
          >
            Cerrar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MainGraph;
